import React from 'react';
import { TokenSelector } from './TokenSelector';
import { TokenIcon } from './TokenIcon';

/**
 * SwapForm - Amount input, stacked pay/receive panel, and quote details.
 *
 * @param {Object} props
 * @param {string} props.selectedStablecoin - Currently selected stablecoin key
 * @param {Function} props.onSelectStablecoin - Callback when stablecoin changes
 * @param {string} props.stablecoinBalance - Formatted balance of selected stablecoin
 * @param {string} props.usdnBalance - Formatted USDN balance
 * @param {string} props.balanceLabel - Label that scopes balances to the selected chain
 * @param {string} props.amount - Current input amount
 * @param {Function} props.onAmountChange - Callback when amount changes
 * @param {string} props.inputError - Validation error message
 * @param {boolean} props.isConnected - Whether wallet is connected
 * @param {Function} props.onMaxClick - Callback for MAX button
 * @param {boolean} props.stablecoinLoading - Whether stablecoin balance is loading
 * @param {boolean} props.usdnLoading - Whether USDN balance is loading
 */
export const SwapForm = ({
  selectedStablecoin,
  onSelectStablecoin,
  stablecoins,
  stablecoinBalance,
  usdnBalance,
  balanceLabel = 'Balance',
  amount,
  onAmountChange,
  inputError,
  isConnected,
  onMaxClick,
  stablecoinLoading,
  usdnLoading,
}) => {
  return (
    <div className="swap-form">
      {/* Amount Input */}
      <div className="amount-section">
        <label className="amount-label" htmlFor="swap-amount">
          Amount
        </label>
        <div className="amount-input-wrapper">
          <input
            id="swap-amount"
            name="swap-amount"
            type="text"
            inputMode="decimal"
            className="amount-input"
            placeholder="0.00"
            value={amount}
            onChange={onAmountChange}
            disabled={!isConnected}
            aria-label="Amount to swap"
            aria-describedby={inputError ? 'amount-error' : undefined}
          />
          <button
            className="max-btn"
            onClick={onMaxClick}
            disabled={!isConnected || stablecoinLoading}
            aria-label="Use maximum available balance"
          >
            MAX
          </button>
        </div>
        {inputError && (
          <div id="amount-error" className="input-error" role="alert">
            {inputError}
          </div>
        )}
      </div>

      {/* Stacked pay/receive panel: From (top) -> Arrow -> To (bottom) */}
      <div className="token-selection-row">
        <TokenSelector
          selectedStablecoin={selectedStablecoin}
          onSelect={onSelectStablecoin}
          stablecoins={stablecoins}
          balance={stablecoinBalance}
          balanceLabel={balanceLabel}
          isConnected={isConnected}
          isLoading={stablecoinLoading}
        />

        <div className="swap-arrow" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 2v10M3 8l4 4 4-4" />
          </svg>
        </div>

        <div className="token-to">
          <div className="token-label">You receive</div>
          <div className="token-info">
            <span className="usdn-mark">
              <TokenIcon symbol="USDN" size={24} />
            </span>
            <div className="token-copy">
              <div className="token-symbol">USDN</div>
              <div className="token-name">Nova USD</div>
            </div>
          </div>
          {isConnected && (
            <div className="balance">
              {balanceLabel}:{' '}
              {usdnLoading ? <span className="loading-text">Loading...</span> : usdnBalance}
            </div>
          )}
        </div>
      </div>

      {/* Quote details */}
      <dl className="swap-details">
        <div className="swap-details-row">
          <dt>Rate</dt>
          <dd className="swap-details-num">1 {selectedStablecoin} ≈ 1.0000 USDN</dd>
        </div>
        <div className="swap-details-row">
          <dt>Network fee</dt>
          <dd>Gas only</dd>
        </div>
        <div className="swap-details-row swap-details-total">
          <dt>You will receive</dt>
          <dd className="swap-details-num">{amount || '0.00'} USDN</dd>
        </div>
      </dl>
    </div>
  );
};
